import type {
  ActionExecutionResult,
  AddTagActionResult,
  AssignV3ActionResult,
  RemoveTagActionResult,
  SetPriorityActionResult,
  SetStatusActionResult,
  SetStatusV3ActionResult,
  StartAiAgentExecutionResult,
} from "@redotech/effect-api-shared/models/shopper-ai/rule-engine-results";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo } from "react";
import * as styles from "../execution-tree-view.module.css";

export const ActionResult = memo(function ActionResult({
  result,
}: {
  result: ActionExecutionResult;
}) {
  switch (result.actionType) {
    case "SET_STATUS":
      return <SetStatusResult result={result as SetStatusActionResult} />;
    case "SET_STATUS_V3":
      return <SetStatusV3Result result={result as SetStatusV3ActionResult} />;
    case "SET_PRIORITY":
      return <SetPriorityResult result={result as SetPriorityActionResult} />;
    case "ADD_TAG":
      return <AddTagResult result={result as AddTagActionResult} />;
    case "REMOVE_TAG":
      return <RemoveTagResult result={result as RemoveTagActionResult} />;
    case "ASSIGN_V3":
      return <AssignV3Result result={result as AssignV3ActionResult} />;
    case "START_AI_AGENT_EXECUTION":
      return (
        <StartAiAgentResult result={result as StartAiAgentExecutionResult} />
      );
    default:
      return <GenericResult result={result} />;
  }
});

const StatusBadge = memo(function StatusBadge({
  status,
}: {
  status: "success" | "failed" | "skipped";
}) {
  return (
    <RedoBadge
      color={
        status === "success"
          ? "success"
          : status === "failed"
            ? "error"
            : "gray"
      }
      size="sm"
      text={status}
    />
  );
});

const SetStatusResult = memo(function SetStatusResult({
  result,
}: {
  result: SetStatusActionResult;
}) {
  return (
    <Flex className={styles.resultRow} dir="column" gap="xxs">
      <Flex align="center" gap="xs">
        <Text fontSize="xs" fontWeight="medium">
          Set Status
        </Text>
        <StatusBadge status={result.status} />
      </Flex>
      <Text fontSize="xs" textColor="tertiary">
        {result.previous} → {result.resulting}
      </Text>
      {result.error && (
        <Text fontSize="xs" textColor="error">
          {result.error}
        </Text>
      )}
    </Flex>
  );
});

const SetStatusV3Result = memo(function SetStatusV3Result({
  result,
}: {
  result: SetStatusV3ActionResult;
}) {
  const status = result.modifiedEntities?.status;
  const snoozedUntil = result.modifiedEntities?.snoozedUntil;
  return (
    <Flex className={styles.resultRow} dir="column" gap="xxs">
      <Flex align="center" gap="xs">
        <Text fontSize="xs" fontWeight="medium">
          Set Status
        </Text>
        <StatusBadge status={result.status} />
      </Flex>
      {status && (
        <Flex align="center" gap="xs">
          <Text fontSize="xs" textColor="tertiary">
            Status set to:
          </Text>
          <RedoBadge color="blue" size="sm" text={status} />
        </Flex>
      )}
      {snoozedUntil && (
        <Text fontSize="xs" textColor="tertiary">
          Snoozed until: {new Date(snoozedUntil).toLocaleString()}
        </Text>
      )}
      {result.error && (
        <Text fontSize="xs" textColor="error">
          {result.error}
        </Text>
      )}
    </Flex>
  );
});

const SetPriorityResult = memo(function SetPriorityResult({
  result,
}: {
  result: SetPriorityActionResult;
}) {
  const priority = result.modifiedEntities?.priority;
  return (
    <Flex className={styles.resultRow} dir="column" gap="xxs">
      <Flex align="center" gap="xs">
        <Text fontSize="xs" fontWeight="medium">
          Set Priority
        </Text>
        <StatusBadge status={result.status} />
      </Flex>
      <Flex align="center" gap="xs">
        <Text fontSize="xs" textColor="tertiary">
          Priority:
        </Text>
        <RedoBadge
          color={priority ? "warning" : "gray"}
          size="sm"
          text={priority ?? "None"}
        />
      </Flex>
      {result.error && (
        <Text fontSize="xs" textColor="error">
          {result.error}
        </Text>
      )}
    </Flex>
  );
});

const AddTagResult = memo(function AddTagResult({
  result,
}: {
  result: AddTagActionResult;
}) {
  const tags = result.modifiedEntities?.tagsAdded;
  return (
    <Flex className={styles.resultRow} dir="column" gap="xxs">
      <Flex align="center" gap="xs">
        <Text fontSize="xs" fontWeight="medium">
          Add Tag
        </Text>
        <StatusBadge status={result.status} />
      </Flex>
      {tags && tags.length > 0 && (
        <Flex align="center" gap="xs" wrap="wrap">
          <Text fontSize="xs" textColor="tertiary">
            Added:
          </Text>
          {tags.map((tag) => (
            <RedoBadge color="blue" key={tag} size="sm" text={tag} />
          ))}
        </Flex>
      )}
      {result.error && (
        <Text fontSize="xs" textColor="error">
          {result.error}
        </Text>
      )}
    </Flex>
  );
});

const RemoveTagResult = memo(function RemoveTagResult({
  result,
}: {
  result: RemoveTagActionResult;
}) {
  const tags = result.modifiedEntities?.tagsRemoved;
  return (
    <Flex className={styles.resultRow} dir="column" gap="xxs">
      <Flex align="center" gap="xs">
        <Text fontSize="xs" fontWeight="medium">
          Remove Tag
        </Text>
        <StatusBadge status={result.status} />
      </Flex>
      {tags && tags.length > 0 && (
        <Flex align="center" gap="xs" wrap="wrap">
          <Text fontSize="xs" textColor="tertiary">
            Removed:
          </Text>
          {tags.map((tag) => (
            <RedoBadge color="gray" key={tag} size="sm" text={tag} />
          ))}
        </Flex>
      )}
      {result.error && (
        <Text fontSize="xs" textColor="error">
          {result.error}
        </Text>
      )}
    </Flex>
  );
});

const AssignV3Result = memo(function AssignV3Result({
  result,
}: {
  result: AssignV3ActionResult;
}) {
  const entities = result.modifiedEntities;
  return (
    <Flex className={styles.resultRow} dir="column" gap="xxs">
      <Flex align="center" gap="xs">
        <Text fontSize="xs" fontWeight="medium">
          Assign
        </Text>
        <StatusBadge status={result.status} />
      </Flex>
      {entities?.assignedUserName && (
        <Flex align="center" gap="xs">
          <Text fontSize="xs" textColor="tertiary">
            Assigned to:
          </Text>
          <RedoBadge color="blue" size="sm" text={entities.assignedUserName} />
        </Flex>
      )}
      {entities?.mode && (
        <Text fontSize="xs" textColor="tertiary">
          Mode: {entities.mode}
          {entities.strategy ? ` (${entities.strategy})` : ""}
          {entities.assigneeType ? ` → ${entities.assigneeType}` : ""}
        </Text>
      )}
      {result.error && (
        <Text fontSize="xs" textColor="error">
          {result.error}
        </Text>
      )}
    </Flex>
  );
});

const StartAiAgentResult = memo(function StartAiAgentResult({
  result,
}: {
  result: StartAiAgentExecutionResult;
}) {
  return (
    <Flex className={styles.resultRow} dir="column" gap="xxs">
      <Flex align="center" gap="xs">
        <Text fontSize="xs" fontWeight="medium">
          AI Response
        </Text>
        <StatusBadge status={result.status} />
      </Flex>
      {result.error && (
        <Text fontSize="xs" textColor="error">
          {result.error}
        </Text>
      )}
    </Flex>
  );
});

const GenericResult = memo(function GenericResult({
  result,
}: {
  result: ActionExecutionResult;
}) {
  return (
    <Flex className={styles.resultRow} dir="column" gap="xxs">
      <Flex align="center" gap="xs">
        <Text className={styles.badge} fontSize="xxs">
          {result.actionType ?? "unknown"}
        </Text>
        <StatusBadge status={result.status} />
      </Flex>
      {result.error && (
        <Text fontSize="xs" textColor="error">
          {result.error}
        </Text>
      )}
    </Flex>
  );
});
